import { useState } from "react";
import { Check, ChevronsUpDown, Building2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Organization {
  id: string;
  name: string;
  display_name?: string;
  subdomain: string;
  is_active: boolean;
}

interface OrganizationSelectorProps {
  value?: string | null;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
  className?: string;
  includeDefault?: boolean;
  defaultLabel?: string;
}

const OrganizationSelector = ({
  value,
  onValueChange,
  placeholder = "Select organization...",
  className,
  includeDefault = true,
  defaultLabel = "Platform Default"
}: OrganizationSelectorProps) => {
  const [open, setOpen] = useState(false);

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['organizations-selector'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, display_name, subdomain, is_active')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data as Organization[];
    }
  });

  const selectedOrganization = organizations.find(org => org.id === value);
  const selectedLabel = value === null 
    ? defaultLabel 
    : selectedOrganization?.display_name || selectedOrganization?.name || "Unknown";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", className)}
          disabled={isLoading}
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">
              {value !== undefined ? selectedLabel : placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search organizations..." />
          <CommandList>
            <CommandEmpty>No organizations found.</CommandEmpty>
            <CommandGroup>
              {includeDefault && (
                <CommandItem
                  value="platform-default"
                  onSelect={() => {
                    onValueChange(null);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === null ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{defaultLabel}</div>
                      <div className="text-sm text-muted-foreground">
                        System-wide default parcel types
                      </div>
                    </div>
                  </div>
                </CommandItem>
              )}
              {organizations.map((org) => (
                <CommandItem
                  key={org.id}
                  value={`${org.name}-${org.subdomain}`}
                  onSelect={() => {
                    onValueChange(org.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === org.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {org.display_name || org.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {org.subdomain}.domain.com
                      </div>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default OrganizationSelector;